import { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';

const shimmerStyle = {
  background: 'linear-gradient(90deg, #f0f2f7 25%, #e2e6ef 50%, #f0f2f7 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.4s infinite',
  borderRadius: '4px',
};

const GroupedBarChart = ({ data, loading = false, ySuffix = '%', yPrefix = '' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (loading || !data || !canvasRef.current) return;

    const existing = Chart.getChart(canvasRef.current);
    if (existing) existing.destroy();

    const chart = new Chart(canvasRef.current, {
      type: 'bar',
      data,
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: { font: { size: 10 }, boxWidth: 10, padding: 10 },
          },
          tooltip: {
            callbacks: {
              label: (c) => `${c.dataset.label}: ${yPrefix}${c.parsed.y}${ySuffix}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              font: { size: 10 },
              callback: (v) => `${yPrefix}${v}${ySuffix}`,
            },
            grid: { color: '#eef1f7' },
          },
          x: {
            ticks: {
              font: { size: 10 },
              maxRotation: 15,
              callback: function (val) {
                const label = this.getLabelForValue(val);
                return label.length > 12 ? label.slice(0, 12) + '…' : label;
              },
            },
            grid: { display: false },
          },
        },
      },
    });

    return () => chart.destroy();
  }, [data, loading, ySuffix, yPrefix]);

  if (loading) {
    return (
      <div className="chart-wrap" style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', padding: '10px 0' }}>
        {[55, 75, 45, 90, 60, 80].map((h, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <div style={{ ...shimmerStyle, width: '100%', height: `${h}%` }} />
            <div style={{ ...shimmerStyle, width: '80%', height: '8px' }} />
          </div>
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="chart-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: '12px' }}>
        No data available
      </div>
    );
  }

  return (
    <div className="chart-wrap">
      <canvas ref={canvasRef} />
    </div>
  );
};

export default GroupedBarChart;
